import {
  AccountSchema,
  AiToolResponseSchema,
  AlternativesResponseSchema,
  MenuSettingResponseSchema,
  MenuSettingsResponseSchema,
  AiToolsResponseSchema,
  ToolRunResponseSchema,
  ApiKeysResponseSchema,
  CustomToneResponseSchema,
  CustomTonesResponseSchema,
  SavedInstructionResponseSchema,
  SavedInstructionsResponseSchema,
  DraftResponseSchema,
  DraftsResponseSchema,
  CLIENT_HEADER,
  CheckoutSettingsSchema,
  CreatedApiKeySchema,
  ExplainResponseSchema,
  PaymentsResponseSchema,
  PlanChangePreviewSchema,
  PlansResponseSchema,
  ProblemSchema,
  RedirectResponseSchema,
  RewriteResponseSchema,
  TranscriptionResponseSchema,
  DictationConfigSchema,
  ImageTextConfigSchema,
  ImageTextResponseSchema,
  UsageBreakdownSchema,
  type DictationConfig,
  type ImageTextConfig,
  type ImageTextQuery,
  type Account,
  type UsageBreakdown,
  type AiTool,
  type Alternative,
  type AlternativesRequest,
  type AiToolInput,
  type Menu,
  type MenuSetting,
  type MenuSettingInput,
  type ToolRunResponse,
  type ApiKey,
  type ClientName,
  type CreatedApiKey,
  type CheckoutChoice,
  type CustomTone,
  type CustomToneInput,
  type SavedInstruction,
  type SavedInstructionInput,
  type Draft,
  type DraftInput,
  type DraftSummary,
  type CheckoutSettings,
  type ClientEvent,
  type ConsentChoice,
  type ErrorCode,
  type ExplainRequest,
  type Explanation,
  type Payment,
  type PlansResponse,
  type PassageContext,
  type PlanChangePreview,
  type RewriteOptions,
  type RewriteResponse,
  type SignInNext,
} from '@lyrmo/shared';
import type { z } from 'zod';
import { ApiError } from './api-error';

export interface ApiClient {
  /** WAV audio of a corrected version, spoken with an AI-generated voice. */
  createSpeech(text: string, options?: { signal?: AbortSignal }): Promise<Blob>;
  getDictationConfig(options?: { signal?: AbortSignal }): Promise<DictationConfig>;
  transcribeAudio(audio: Blob, options?: { signal?: AbortSignal }): Promise<string>;
  getImageTextConfig(options?: { signal?: AbortSignal }): Promise<ImageTextConfig>;
  /**
   * Reads the text in an uploaded image, or with `mode: 'describe'` describes what it shows, in
   * `language`.
   */
  extractImageText(
    image: Blob,
    options?: { signal?: AbortSignal } & Partial<ImageTextQuery>,
  ): Promise<string>;
  /** With `context`, `text` is a passage of the writing around it, such as a selection. */
  createRewrite(
    text: string,
    options?: { signal?: AbortSignal; options?: RewriteOptions; context?: PassageContext },
  ): Promise<RewriteResponse>;
  /** Why each change between a text and its rewrite helps, by the change's index. */
  explainChanges(
    request: ExplainRequest,
    options?: { signal?: AbortSignal },
  ): Promise<Explanation[]>;
  /** Other ways to say each change between a text and its rewrite, by the change's index. */
  suggestAlternatives(
    request: AlternativesRequest,
    options?: { signal?: AbortSignal },
  ): Promise<Alternative[]>;
  /** The signed-in writer's custom tones, oldest first. */
  listCustomTones(): Promise<CustomTone[]>;
  createCustomTone(tone: CustomToneInput): Promise<CustomTone>;
  updateCustomTone(id: string, tone: CustomToneInput): Promise<CustomTone>;
  deleteCustomTone(id: string): Promise<void>;
  /** The signed-in writer's saved instructions, oldest first. */
  listSavedInstructions(): Promise<SavedInstruction[]>;
  createSavedInstruction(instruction: SavedInstructionInput): Promise<SavedInstruction>;
  updateSavedInstruction(id: string, instruction: SavedInstructionInput): Promise<SavedInstruction>;
  deleteSavedInstruction(id: string): Promise<void>;
  /** The drafts the signed-in writer keeps in their account, without their text, last saved first. */
  listDrafts(): Promise<DraftSummary[]>;
  /** One of the writer's drafts, with its text. */
  getDraft(id: string): Promise<Draft>;
  createDraft(draft: DraftInput): Promise<Draft>;
  updateDraft(id: string, draft: DraftInput): Promise<Draft>;
  deleteDraft(id: string): Promise<void>;
  /**
   * Formats `text` with an AI tool: a built-in one, by name, or one of the writer's own, by id,
   * or else the way an instruction says, just this once, without making a tool.
   * With `context`, `text` is a passage of the writing around it, such as a selection.
   */
  runTool(
    text: string,
    tool: string | { readonly instruction: string },
    options?: { signal?: AbortSignal; context?: PassageContext },
  ): Promise<ToolRunResponse>;
  /** The signed-in writer's own AI tools, oldest first. */
  listAiTools(): Promise<AiTool[]>;
  createAiTool(tool: AiToolInput): Promise<AiTool>;
  updateAiTool(id: string, tool: AiToolInput): Promise<AiTool>;
  deleteAiTool(id: string): Promise<void>;
  /** Which choices in a menu, built-in or the writer's own, they hid or pinned; the rest are as they started. */
  listMenuSettings(menu: Menu): Promise<MenuSetting[]>;
  /** Hides or pins one choice in a menu: a built-in one by name, or one of the writer's own by id. */
  updateMenuSetting(menu: Menu, id: string, setting: MenuSettingInput): Promise<MenuSetting>;
  getAccount(): Promise<Account>;
  /** Today's credits, with what they went on. */
  getUsageBreakdown(): Promise<UsageBreakdown>;
  getPlans(): Promise<PlansResponse>;
  /** Emails a sign-in link and code; `next` continues to checkout after the link. */
  requestSignInLink(email: string, next?: SignInNext): Promise<void>;
  /** Signs in with the code from the sign-in email. */
  signInWithCode(email: string, code: string): Promise<void>;
  signOut(): Promise<void>;
  /** Returns the checkout page for a plan, billed monthly or yearly. */
  startCheckout(choice: CheckoutChoice): Promise<string>;
  /** How to open the payment provider's checkout on the app's own pages. */
  getCheckoutSettings(): Promise<CheckoutSettings>;
  /** What a subscriber would be charged, or credited, now for switching plans. */
  previewPlanChange(choice: CheckoutChoice): Promise<PlanChangePreview>;
  /** Switches a subscriber's plan, billing the difference for the rest of the period. */
  changePlan(choice: CheckoutChoice): Promise<void>;
  /** Stops a subscription renewing; it lasts until the end of the period it's paid for. */
  cancelSubscription(): Promise<void>;
  /** Takes back a cancellation, so the subscription renews after all. */
  resumeSubscription(): Promise<void>;
  /** A link to the payment provider's page for changing how a subscriber pays. */
  paymentMethodUrl(): string;
  /** A subscriber's latest payments, newest first. */
  listPayments(): Promise<Payment[]>;
  /** A link to a payment's invoice. */
  invoiceUrl(paymentId: string): string;
  /** The signed-in account's API keys, without the keys themselves. */
  listApiKeys(): Promise<ApiKey[]>;
  /** Makes an API key, returning it with the key itself, which is shown only this once. */
  createApiKey(name: string): Promise<CreatedApiKey>;
  /** Revokes an API key, so requests with it stop working. */
  revokeApiKey(id: string): Promise<void>;
  /**
   * Tells the API what the writer did, for Lyrmo's own insights: never the text. `keepalive`
   * lets the request finish as the page closes.
   */
  recordEvents(events: readonly ClientEvent[], options?: { keepalive?: boolean }): Promise<void>;
  /** Tells the API the choice just made about Lyrmo's own insights, which it counts as no one's. */
  recordConsent(choice: ConsentChoice): Promise<void>;
}

export type RewriteClient = Pick<ApiClient, 'createRewrite' | 'explainChanges'> &
  Partial<
    Pick<
      ApiClient,
      | 'createSpeech'
      | 'transcribeAudio'
      | 'getDictationConfig'
      | 'getImageTextConfig'
      | 'extractImageText'
      | 'getAccount'
      | 'runTool'
      | 'suggestAlternatives'
    >
  >;

export interface ApiClientOptions {
  /** Origin of the API when it isn't served alongside the app. */
  readonly baseUrl?: string;
  readonly fetch?: typeof fetch;
  /** Which of Lyrmo's clients is asking, told to the API for the admin dashboard's totals. */
  readonly clientName?: ClientName;
}

// For error responses that aren't problem+json, such as a proxy's HTML error page.
/** Where each menu's settings are served, under /api/v1. */
const MENU_PATHS: Readonly<Record<Menu, string>> = {
  tools: 'tool-settings',
  tones: 'tone-settings',
  instructions: 'instruction-settings',
};

const CODES_BY_STATUS: Partial<Record<number, ErrorCode>> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  402: 'CREDIT_LIMIT_REACHED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  413: 'PAYLOAD_TOO_LARGE',
  415: 'UNSUPPORTED_MEDIA_TYPE',
  422: 'REWRITE_REFUSED',
  429: 'RATE_LIMITED',
  502: 'UPSTREAM_ERROR',
  503: 'SERVICE_UNAVAILABLE',
  504: 'UPSTREAM_TIMEOUT',
};

/** Parses a Retry-After header, given either as seconds or as an HTTP date. */
export function parseRetryAfter(value: string | null, now = Date.now()): number | undefined {
  if (!value) return undefined;
  if (/^\d+$/.test(value.trim())) return Number(value);
  const date = Date.parse(value);
  return Number.isNaN(date) ? undefined : Math.max(0, Math.ceil((date - now) / 1000));
}

function errorFromResponse(response: Response, body: unknown): ApiError {
  const problem = ProblemSchema.safeParse(body);
  const status = response.status;
  const retryAfter =
    problem.data?.retryAfter ?? parseRetryAfter(response.headers.get('retry-after'));

  if (problem.success) {
    return new ApiError(problem.data.code, problem.data.detail ?? problem.data.title, {
      status,
      retryAfter,
      usage: problem.data.usage,
    });
  }
  const code = CODES_BY_STATUS[status] ?? (status >= 500 ? 'INTERNAL_ERROR' : 'BAD_REQUEST');
  const message =
    code === 'RATE_LIMITED'
      ? 'Too many requests. Please wait a moment and try again.'
      : code === 'PAYLOAD_TOO_LARGE'
        ? 'The server rejected this upload because its request-size limit was exceeded.'
        : 'Something went wrong. Please try again.';
  return new ApiError(code, message, { status, retryAfter });
}

interface RequestOptions<T> {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly binary?: Blob;
  readonly responseType?: 'blob';
  /** Validates the response body; omit for responses without one. */
  readonly schema?: z.ZodType<T>;
  readonly signal?: AbortSignal;
  /** Lets the request finish after the page that sent it closes. */
  readonly keepalive?: boolean;
}

/** Sends requests to the API and decodes what comes back, for each of the API's clients. */
export function createTransport({
  baseUrl = '',
  fetch: send = (input, init) => fetch(input, init),
  clientName,
}: ApiClientOptions = {}) {
  return async function request<T>(
    path: string,
    {
      method = 'GET',
      body,
      binary,
      schema,
      signal,
      responseType,
      keepalive,
    }: RequestOptions<T> = {},
  ): Promise<T> {
    let response: Response;
    try {
      response = await send(`${baseUrl}${path}`, {
        method,
        headers: {
          accept:
            responseType === 'blob' ? 'audio/wav, application/problem+json' : 'application/json',
          ...(body !== undefined && { 'content-type': 'application/json' }),
          ...(binary && { 'content-type': binary.type || 'application/octet-stream' }),
          ...(clientName && { [CLIENT_HEADER]: clientName }),
        },
        ...(body !== undefined && { body: JSON.stringify(body) }),
        ...(binary && { body: binary }),
        // The session is a cookie; include it when the API is on another origin too.
        credentials: 'include',
        signal,
        ...(keepalive && { keepalive }),
      });
    } catch (error) {
      if (signal?.aborted) throw error;
      throw new ApiError(
        'NETWORK_ERROR',
        'Couldn’t reach Lyrmo. Check your connection and try again.',
        { cause: error },
      );
    }

    if (response.ok && responseType === 'blob') {
      const blob = await response.blob();
      if (!blob.size || blob.type.split(';', 1)[0] !== 'audio/wav') {
        throw new ApiError('INVALID_RESPONSE', 'Speech could not be loaded. Please try again.', {
          status: response.status,
        });
      }
      return blob as T;
    }
    const payload: unknown = await response.json().catch(() => undefined);
    if (!response.ok) throw errorFromResponse(response, payload);
    if (!schema) return undefined as T;

    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError('INVALID_RESPONSE', 'Something went wrong. Please try again.', {
        status: response.status,
        cause: parsed.error,
      });
    }
    return parsed.data;
  };
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const { baseUrl = '' } = options;
  const request = createTransport(options);

  return {
    createSpeech: (text, { signal } = {}) =>
      request<Blob>('/api/v1/speech', {
        method: 'POST',
        body: { text },
        responseType: 'blob',
        signal,
      }),
    getDictationConfig: ({ signal } = {}) =>
      request('/api/v1/dictation/config', { schema: DictationConfigSchema, signal }),
    transcribeAudio: async (audio, { signal } = {}) =>
      (
        await request('/api/v1/transcriptions', {
          method: 'POST',
          binary: audio,
          signal,
          schema: TranscriptionResponseSchema,
        })
      ).text,
    getImageTextConfig: ({ signal } = {}) =>
      request('/api/v1/image-text/config', { schema: ImageTextConfigSchema, signal }),
    extractImageText: async (image, { signal, mode, language } = {}) => {
      // Without a mode, the server reads the image's text.
      const query = String(
        new URLSearchParams({ ...(mode && { mode }), ...(language && { language }) }),
      );
      return (
        await request(`/api/v1/image-text${query && `?${query}`}`, {
          method: 'POST',
          binary: image,
          signal,
          schema: ImageTextResponseSchema,
        })
      ).text;
    },
    createRewrite: (text, { signal, options, context } = {}) =>
      request('/api/v1/rewrites', {
        method: 'POST',
        body: { text, ...(options && { options }), ...(context && { context }) },
        schema: RewriteResponseSchema,
        signal,
      }),
    explainChanges: async (explainRequest, { signal } = {}) =>
      (
        await request('/api/v1/explanations', {
          method: 'POST',
          body: explainRequest,
          schema: ExplainResponseSchema,
          signal,
        })
      ).explanations,
    suggestAlternatives: async (alternativesRequest, { signal } = {}) =>
      (
        await request('/api/v1/alternatives', {
          method: 'POST',
          body: alternativesRequest,
          schema: AlternativesResponseSchema,
          signal,
        })
      ).alternatives,
    listCustomTones: async () =>
      (await request('/api/v1/tones', { schema: CustomTonesResponseSchema })).tones,
    createCustomTone: async (tone) =>
      (
        await request('/api/v1/tones', {
          method: 'POST',
          body: tone,
          schema: CustomToneResponseSchema,
        })
      ).tone,
    updateCustomTone: async (id, tone) =>
      (
        await request(`/api/v1/tones/${encodeURIComponent(id)}`, {
          method: 'PUT',
          body: tone,
          schema: CustomToneResponseSchema,
        })
      ).tone,
    deleteCustomTone: (id) =>
      request(`/api/v1/tones/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    listSavedInstructions: async () =>
      (await request('/api/v1/instructions', { schema: SavedInstructionsResponseSchema }))
        .instructions,
    createSavedInstruction: async (instruction) =>
      (
        await request('/api/v1/instructions', {
          method: 'POST',
          body: instruction,
          schema: SavedInstructionResponseSchema,
        })
      ).instruction,
    updateSavedInstruction: async (id, instruction) =>
      (
        await request(`/api/v1/instructions/${encodeURIComponent(id)}`, {
          method: 'PUT',
          body: instruction,
          schema: SavedInstructionResponseSchema,
        })
      ).instruction,
    deleteSavedInstruction: (id) =>
      request(`/api/v1/instructions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    listDrafts: async () =>
      (await request('/api/v1/drafts', { schema: DraftsResponseSchema })).drafts,
    getDraft: async (id) =>
      (await request(`/api/v1/drafts/${encodeURIComponent(id)}`, { schema: DraftResponseSchema }))
        .draft,
    createDraft: async (draft) =>
      (
        await request('/api/v1/drafts', {
          method: 'POST',
          body: draft,
          schema: DraftResponseSchema,
        })
      ).draft,
    updateDraft: async (id, draft) =>
      (
        await request(`/api/v1/drafts/${encodeURIComponent(id)}`, {
          method: 'PUT',
          body: draft,
          schema: DraftResponseSchema,
        })
      ).draft,
    deleteDraft: (id) => request(`/api/v1/drafts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    runTool: (text, tool, { signal, context } = {}) =>
      request('/api/v1/tool-runs', {
        method: 'POST',
        body: {
          text,
          ...(typeof tool === 'string' ? { tool } : { instruction: tool.instruction }),
          ...(context && { context }),
        },
        schema: ToolRunResponseSchema,
        signal,
      }),
    listAiTools: async () =>
      (await request('/api/v1/tools', { schema: AiToolsResponseSchema })).tools,
    createAiTool: async (tool) =>
      (
        await request('/api/v1/tools', {
          method: 'POST',
          body: tool,
          schema: AiToolResponseSchema,
        })
      ).tool,
    updateAiTool: async (id, tool) =>
      (
        await request(`/api/v1/tools/${encodeURIComponent(id)}`, {
          method: 'PUT',
          body: tool,
          schema: AiToolResponseSchema,
        })
      ).tool,
    deleteAiTool: (id) => request(`/api/v1/tools/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    listMenuSettings: async (menu) =>
      (await request(`/api/v1/${MENU_PATHS[menu]}`, { schema: MenuSettingsResponseSchema }))
        .settings,
    updateMenuSetting: async (menu, id, setting) =>
      (
        await request(`/api/v1/${MENU_PATHS[menu]}/${encodeURIComponent(id)}`, {
          method: 'PUT',
          body: setting,
          schema: MenuSettingResponseSchema,
        })
      ).setting,
    getAccount: () => request('/api/v1/me', { schema: AccountSchema }),
    getUsageBreakdown: () => request('/api/v1/me/usage', { schema: UsageBreakdownSchema }),
    getPlans: () => request('/api/v1/billing/plans', { schema: PlansResponseSchema }),
    requestSignInLink: (email, next) =>
      request('/api/v1/auth/email-link', { method: 'POST', body: { email, next } }),
    signInWithCode: (email, code) =>
      request('/api/v1/auth/code', { method: 'POST', body: { email, code } }),
    signOut: () => request('/api/v1/auth/sign-out', { method: 'POST' }),
    startCheckout: async ({ plan, interval }) =>
      (
        await request('/api/v1/billing/checkout', {
          method: 'POST',
          body: { plan, interval },
          schema: RedirectResponseSchema,
        })
      ).url,
    getCheckoutSettings: () =>
      request('/api/v1/billing/checkout-settings', { schema: CheckoutSettingsSchema }),
    previewPlanChange: ({ plan, interval }) =>
      request('/api/v1/billing/plan-change/preview', {
        method: 'POST',
        body: { plan, interval },
        schema: PlanChangePreviewSchema,
      }),
    changePlan: ({ plan, interval }) =>
      request('/api/v1/billing/plan-change', { method: 'POST', body: { plan, interval } }),
    cancelSubscription: () => request('/api/v1/billing/cancel', { method: 'POST' }),
    resumeSubscription: () => request('/api/v1/billing/resume', { method: 'POST' }),
    paymentMethodUrl: () => `${baseUrl}/api/v1/billing/payment-method`,
    listPayments: async () =>
      (await request('/api/v1/billing/payments', { schema: PaymentsResponseSchema })).payments,
    invoiceUrl: (paymentId) =>
      `${baseUrl}/api/v1/billing/payments/${encodeURIComponent(paymentId)}/invoice`,
    listApiKeys: async () =>
      (await request('/api/v1/api-keys', { schema: ApiKeysResponseSchema })).keys,
    createApiKey: (name) =>
      request('/api/v1/api-keys', { method: 'POST', body: { name }, schema: CreatedApiKeySchema }),
    revokeApiKey: (id) =>
      request(`/api/v1/api-keys/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    recordEvents: (events, { keepalive } = {}) =>
      request('/api/v1/events', { method: 'POST', body: { events }, keepalive }),
    recordConsent: (choice) =>
      request('/api/v1/consent', { method: 'POST', body: { choice }, keepalive: true }),
  };
}
